/**
 * @file: useTaskCardSize.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useLayoutContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';

// Card-grid sizing rules:
//   - Pack as many columns as possible while keeping each card at least
//     `kMinCellWidth` wide. iPad-portrait gets 2 cols, "large screen" 3.
//   - Cap at `kMaxColumns` columns so a denser desktop grid doesn't dilute
//     each card into a thin slice; extra horizontal space becomes
//     breathing room instead of more columns.
//   - When the resulting card would exceed `kMaxCellWidth`, freeze the
//     grid at `kMaxColumns * kMaxCellWidth` and center it — that's the
//     "wide screen with whitespace" look.
const kMinCellWidth = 320;
const kMaxCellWidth = 380;
const kMaxColumns = 4;

export const useTaskListLayout = () => {
  const { rpx } = useResponsiveContext();
  const containerWidth = useLayoutContainerWidth();
  const minCell = rpx(kMinCellWidth);
  const maxCell = rpx(kMaxCellWidth);

  const naturalCols = Math.max(1, Math.floor(containerWidth / minCell));
  const numberOfColumns = Math.min(kMaxColumns, naturalCols);
  const cardWidth = containerWidth / numberOfColumns;
  // Cap only applies in multi-column grids. On phones the layout is
  // 1-column and the user expects edge-to-edge cards — capping would
  // create awkward side whitespace inside a narrow viewport.
  const shouldCap = numberOfColumns > 1 && cardWidth > maxCell;
  const width = shouldCap ? numberOfColumns * maxCell : containerWidth;

  return {
    width,
    containerWidth,
    numberOfColumns,
  };
};
